import type { ComponentProps } from 'react';
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import { cn } from '../../lib/utils.js';

/** Base UI-backed tooltip — used internally by Sidebar for collapsed-icon labels. See BaseButton.tsx. */
export function Tooltip({ ...props }: ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

export function TooltipTrigger({ ...props }: ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

export function TooltipContent({
  className,
  side = 'top',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  children,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Popup> &
  Pick<
    ComponentProps<typeof TooltipPrimitive.Positioner>,
    'align' | 'alignOffset' | 'side' | 'sideOffset'
  >) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            'relative z-50 inline-flex w-fit max-w-xs origin-(--transform-origin) items-center gap-1.5 rounded-md bg-text px-3 py-1.5 text-xs text-bg',
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className,
          )}
          {...props}
        >
          {children}
          {/* The library only positions the arrow along the alignment axis (left for a
           * top/bottom popup, top for a left/right one) via inline style — it leaves the
           * perpendicular axis (flush against the popup's anchor-facing edge) to us, keyed off
           * the data-side attribute it sets on this element. Without these, the arrow keeps its
           * default in-flow position instead of sitting on the edge it should point from. */}
          <TooltipPrimitive.Arrow
            className={cn(
              'absolute z-50 size-2.5 rotate-45 rounded-[2px] bg-text fill-text',
              'data-[side=top]:-bottom-[5px] data-[side=bottom]:-top-[5px]',
              'data-[side=left]:-right-[5px] data-[side=right]:-left-[5px]',
            )}
          />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

export { TooltipPrimitive };
